import { createContext } from "react";
import { compoundEventKey } from "./providers/types";
import { pickProvider } from "./providers";

/**
 * In-memory enrichment cache + dispatch layer for useEventTitles.
 *
 * Privacy invariant — guarded by tests in EnrichmentCache.test.js:
 *   The cache lives in a JS Map only. We never write to localStorage,
 *   sessionStorage, or IndexedDB. Persisting titles across tab/page
 *   reloads would defeat the privacy guarantee from
 *   docs/calendar-cache-privacy-plan.md by pushing the leak surface
 *   into the user's browser disk.
 *
 * Behavior contract:
 *   - request(reqs)           queue lookups; microtask-coalesces into batched
 *                             provider calls grouped by (provider, calendarId).
 *   - get(req)                synchronous read; undefined if never requested.
 *   - subscribe(cb)           change notification; cb fires after a batch
 *                             completes (one notify per flushed batch, not
 *                             per individual entry).
 *   - invalidate(provider, calendarId?) — drop matching cached entries; next
 *                             request() refetches.
 */

export const DEFAULT_TTL_MS = 5 * 60 * 1000;

/**
 * @typedef {Object} EnrichmentCacheContextValue
 * @property {(reqs: import('./providers/types').EnrichmentRequest[]) => void} request
 * @property {(req: import('./providers/types').EnrichmentRequest) => import('./providers/types').EnrichmentResult | undefined} get
 * @property {(cb: () => void) => () => void} subscribe
 * @property {(providerId: string, calendarId?: string) => void} invalidate
 * @property {() => void} clear
 */

export const EnrichmentCacheContext = createContext(
  /** @type {EnrichmentCacheContextValue | null} */ (null),
);

/**
 * Build the cache instance and wire dispatch. Pure (no React) so it can be
 * unit-tested without RTL.
 *
 * `tokenProviders` may be supplied in either of two shapes — pick whichever
 * fits the consumer:
 *   - As a `Record<providerId, () => Promise<credentials>>` map for tests
 *     and one-shot setup.
 *   - As a `(providerId) => () => Promise<credentials>` resolver for the
 *     React provider, which needs ref-style indirection so the cache
 *     always sees the latest map without re-initializing.
 *
 * @param {{
 *   registry: Record<string, import('./providers/types').EnrichmentProvider>,
 *   tokenProviders: Record<string, () => Promise<object|null>> | ((providerId: string) => (() => Promise<object|null>) | undefined),
 *   ttlMs?: number,
 *   now?: () => number,
 *   scheduleFlush?: (cb: () => void) => void,
 * }} opts
 */
export function createEnrichmentCache(opts) {
  const {
    registry,
    tokenProviders,
    ttlMs = DEFAULT_TTL_MS,
    now = () => Date.now(),
    scheduleFlush = (cb) => queueMicrotask(cb),
  } = opts;

  function resolveTokenProvider(providerId) {
    if (typeof tokenProviders === "function") return tokenProviders(providerId);
    if (tokenProviders && typeof tokenProviders === "object") {
      return tokenProviders[providerId];
    }
    return undefined;
  }

  /** @type {Map<string, import('./providers/types').EnrichmentResult>} */
  const cache = new Map();
  /** @type {Map<string, Promise<void>>} key = "${provider}|${calendarId}|${eventId}", value = in-flight promise */
  const inflight = new Map();
  /** @type {Set<() => void>} */
  const subscribers = new Set();

  /** @type {Array<import('./providers/types').EnrichmentRequest>} */
  let queue = [];
  let flushScheduled = false;

  function notify() {
    for (const cb of subscribers) {
      try {
        cb();
      } catch {
        // Don't let one subscriber's throw break the others.
      }
    }
  }

  function isFresh(entry) {
    if (!entry) return false;
    if (entry.error) return false; // errors are not "fresh" — retry on next access
    return now() - entry.fetchedAt < ttlMs;
  }

  function get(req) {
    if (!req) return undefined;
    return cache.get(compoundEventKey(req.provider, req.calendarId, req.eventId));
  }

  function setEntry(provider, calendarId, eventId, value) {
    cache.set(compoundEventKey(provider, calendarId, eventId), value);
  }

  function flush() {
    flushScheduled = false;
    const pending = queue;
    queue = [];

    /** @type {Map<string, { provider: string, calendarId: string, eventIds: Set<string> }>} */
    const groups = new Map();
    for (const req of pending) {
      if (!req || !req.provider || !req.calendarId || !req.eventId) continue;
      const compoundKey = compoundEventKey(req.provider, req.calendarId, req.eventId);
      if (isFresh(cache.get(compoundKey))) continue;
      if (inflight.has(compoundKey)) continue;
      const groupKey = `${req.provider}|${req.calendarId}`;
      let group = groups.get(groupKey);
      if (!group) {
        group = { provider: req.provider, calendarId: req.calendarId, eventIds: new Set() };
        groups.set(groupKey, group);
      }
      group.eventIds.add(req.eventId);
    }

    if (groups.size === 0) {
      notify();
      return;
    }

    for (const group of groups.values()) {
      const eventIdList = [...group.eventIds];
      const fetchPromise = (async () => {
        try {
          const provider = pickProvider(registry, group.provider);
          if (!provider) {
            const fetchedAt = now();
            for (const eventId of eventIdList) {
              setEntry(group.provider, group.calendarId, eventId, {
                title: null,
                fetchedAt,
                error: "permission_denied",
              });
            }
            return;
          }
          const tokenFn = resolveTokenProvider(group.provider);
          let credentials = null;
          if (typeof tokenFn === "function") {
            try {
              credentials = await tokenFn();
            } catch {
              credentials = null;
            }
          }
          const map = await provider.enrichByIds({
            calendarId: group.calendarId,
            eventIds: eventIdList,
            credentials,
          });
          const fetchedAt = now();
          for (const eventId of eventIdList) {
            const result = map.get(eventId);
            if (result) {
              setEntry(group.provider, group.calendarId, eventId, result);
            } else {
              setEntry(group.provider, group.calendarId, eventId, {
                title: null,
                fetchedAt,
                error: "not_found",
              });
            }
          }
        } catch {
          const fetchedAt = now();
          for (const eventId of eventIdList) {
            setEntry(group.provider, group.calendarId, eventId, {
              title: null,
              fetchedAt,
              error: "network",
            });
          }
        }
      })();

      for (const eventId of eventIdList) {
        const k = compoundEventKey(group.provider, group.calendarId, eventId);
        inflight.set(k, fetchPromise);
        fetchPromise.finally(() => inflight.delete(k));
      }
    }

    Promise.allSettled([...inflight.values()]).then(notify);
  }

  function request(reqs) {
    if (!Array.isArray(reqs) || reqs.length === 0) return;
    queue.push(...reqs);
    if (!flushScheduled) {
      flushScheduled = true;
      scheduleFlush(flush);
    }
  }

  function subscribe(cb) {
    subscribers.add(cb);
    return () => {
      subscribers.delete(cb);
    };
  }

  function invalidate(providerId, calendarId) {
    if (!providerId) return;
    const prefix = calendarId ? `${providerId}|${calendarId}|` : `${providerId}|`;
    for (const key of [...cache.keys()]) {
      if (key.startsWith(prefix)) cache.delete(key);
    }
    notify();
  }

  // Defense-in-depth for cross-account isolation: when the signed-in user
  // changes (e.g. sign-out then sign-in as a different user on the same tab),
  // every cached title belongs to the previous user's session and must be
  // dropped before the new user's UI starts hydrating. invalidate() can do
  // this per-provider, but callers shouldn't have to enumerate the registry —
  // clear() is the unconditional, all-providers reset.
  function clear() {
    if (cache.size === 0) {
      notify();
      return;
    }
    cache.clear();
    notify();
  }

  return { request, get, subscribe, invalidate, clear };
}
